import { FC } from 'react';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';

type ServicesCtaPrimary = {
  kicker?: string;
  headline?: import('@prismicio/client').RichTextField;
  description?: import('@prismicio/client').RichTextField;
  primary_cta?: import('@prismicio/client').LinkField;
  cta_label?: string;
  secondary_cta?: import('@prismicio/client').LinkField;
  secondary_cta_label?: string;
  tertiary_cta?: import('@prismicio/client').LinkField;
  tertiary_cta_label?: string;
  background_image?: import('@prismicio/client').ImageField;
};

type ServicesCtaSlice = {
  slice_type: string;
  variation: string;
  primary: ServicesCtaPrimary;
};

const ServicesCta: FC<{ slice: ServicesCtaSlice }> = ({ slice }) => {
  const {
    kicker,
    headline,
    description,
    primary_cta,
    cta_label,
    secondary_cta,
    secondary_cta_label,
    tertiary_cta,
    tertiary_cta_label,
    background_image,
  } = slice.primary;

  const hasPrimary = isFilled.link(primary_cta) && cta_label;
  const hasSecondary = isFilled.link(secondary_cta) && secondary_cta_label;
  const hasTertiary = isFilled.link(tertiary_cta) && tertiary_cta_label;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#01060a] py-16 md:py-24"
    >
      {isFilled.image(background_image) && (
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <PrismicNextImage
            field={background_image}
            fill
            className="object-cover"
            sizes="100vw"
            fallbackAlt=""
          />
        </div>
      )}

      <div className="relative mx-auto flex w-full max-w-[1140px] flex-col items-center gap-6 px-4 text-center md:px-8">
        {kicker && (
          <div className="rounded-full border border-[#00f557] px-5 py-2">
            <span className="text-[12px] font-normal uppercase tracking-[0.04em] text-white md:text-[14px]">
              {kicker}
            </span>
          </div>
        )}

        {isFilled.richText(headline) && (
          <PrismicRichText
            field={headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="max-w-[1040px] text-[32px] font-semibold leading-[1.15] text-white md:text-[48px]">
                  {children}
                </h2>
              ),
            }}
          />
        )}

        {isFilled.richText(description) && (
          <div className="w-full">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-pretty text-[15px] leading-[1.6] text-white/80 md:text-[16px] [&:not(:first-child)]:mt-4">
                    {children}
                  </p>
                ),
                // Bold text renders as the green emphasis line.
                strong: ({ children }) => (
                  <strong className="font-bold text-[#00f557]">
                    {children}
                  </strong>
                ),
                hyperlink: ({ children, node }) => (
                  <a
                    href={node.data.url ?? '#'}
                    className="text-[#00f557] underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
              }}
            />
          </div>
        )}

        {(hasPrimary || hasSecondary || hasTertiary) && (
          <div className="flex flex-col items-stretch justify-center gap-4 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
            {hasPrimary && (
              <PrismicNextLink
                field={primary_cta}
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/60 bg-transparent px-7 py-3.5 text-[15px] font-bold leading-[1.5] text-white transition-colors hover:border-white hover:bg-white/10 md:text-[16px]"
              >
                <ArrowRightIcon className="h-4 w-4" />
                {cta_label}
              </PrismicNextLink>
            )}
            {hasSecondary && (
              <PrismicNextLink
                field={secondary_cta}
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/60 bg-transparent px-7 py-3.5 text-[15px] font-bold leading-[1.5] text-white transition-colors hover:border-white hover:bg-white/10 md:text-[16px]"
              >
                <ArrowRightIcon className="h-4 w-4" />
                {secondary_cta_label}
              </PrismicNextLink>
            )}
            {hasTertiary && (
              <PrismicNextLink
                field={tertiary_cta}
                className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/60 bg-transparent px-7 py-3.5 text-[15px] font-bold leading-[1.5] text-white transition-colors hover:border-white hover:bg-white/10 md:text-[16px]"
              >
                <ArrowRightIcon className="h-4 w-4" />
                {tertiary_cta_label}
              </PrismicNextLink>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesCta;
